import camelcaseKeys from 'camelcase-keys'

import type { Statement } from '@/modules/projects'
import type {
  Edit,
  EditPreview,
  EditRequestDTO,
} from '@/modules/projects/stores/types'
import { executeGet, executePost } from '@/services/lukita'
import { addOrUpdateItems } from '@/utils'
import type {
  EditsActions,
  EditsLoaders,
  EditsMutations,
  ProjectStore,
  ProjectStoreCreator,
} from './types'

interface EditResponseDTO {
  edit: unknown
  statements: unknown[]
}

const newestFirst = (edits: Edit[]) =>
  [...edits].sort((one, other) => other.id - one.id)

const camel = <Shape>(body: unknown) =>
  camelcaseKeys(body as Record<string, unknown>, {
    deep: true,
  }) as unknown as Shape

const openSlug = (get: () => ProjectStore) => {
  const project = get().project
  if (!project) throw new Error('No project is open')
  return project.slug
}

// An edit comes back with the statements it touched, so the page redraws without waiting for the
// websocket. The directory is fetched again because an edit can rename, add or drop an account.
const absorb = async (get: () => ProjectStore, response: Response) => {
  const body = (await response.json()) as EditResponseDTO
  const edit = camel<Edit>(body.edit)
  const { actions, loaders, project } = get()
  for (const statement of body.statements) {
    actions.setStatement(camel<Statement>(statement))
  }
  actions.setEdit(edit)
  if (project) await loaders.fetchDirectoryAccounts(project.slug)
  return edit
}

export const editsActions: ProjectStoreCreator<EditsActions> = (set) => ({
  setEdits: (edits) => set({ edits: newestFirst(edits) }),
  setEdit: (edit) =>
    set((state) => ({
      edits: newestFirst(addOrUpdateItems(state.edits, [edit])),
    })),
})

export const editsMutations: ProjectStoreCreator<EditsMutations> = (
  _set,
  get,
) => ({
  previewEdit: async (request) => {
    const response = await executePost(
      `/projects/${openSlug(get)}/edits/preview`,
      request,
    )
    return camel<EditPreview>(await response.json())
  },

  commitEdit: async (request: EditRequestDTO) =>
    absorb(get, await executePost(`/projects/${openSlug(get)}/edits`, request)),

  undoEdit: async (id) =>
    absorb(
      get,
      await executePost(`/projects/${openSlug(get)}/edits/${id}/undo`),
    ),
})

export const editsLoaders: ProjectStoreCreator<EditsLoaders> = (_set, get) => ({
  fetchEdits: async (slug) => {
    const response = await executeGet(`/projects/${slug}/edits`)
    get().actions.setEdits(camel<Edit[]>(await response.json()))
  },
})
